import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import DateTimeIcon from 'assets/images/calendar/icon-datetime.svg';
import GuestCountIcon from 'assets/images/calendar/icon-guest-count.svg';
import LeadAssignmentIcon from 'assets/images/calendar/icon-lead-assignment.svg';
import LocationIcon from 'assets/images/calendar/icon-location.svg';
import ProjectLeadIcon from 'assets/images/calendar/icon-project-lead.svg';
import ProjectServiceTypeIcon from 'assets/images/calendar/icon-project-type.svg';
import { EventDateTimeDisplay } from './EventDateTimeDisplay';

dayjs.extend(utc);
dayjs.extend(timezone);

const BookedProjectEventCard = ({ eventDetails, userDetails }) => {
  const {
    title: projectName,
    allDay = false,
    startStr = '',
    endStr = '',
    extendedProps = {},
  } = eventDetails || {};
  const { timeFormat } = userDetails || {};
  const {
    serviceType = '',
    weddingAddress: venue = '',
    weddingLocation: location = {},
    weddingGuestCount: guestCount = 0,
    weddingPartners: partners = [],
    image306: image = null,
    projectId,
    displayTz,
  } = extendedProps;
  const lead = partners.find(p => p.isLead);
  const otherPartners = partners.filter(p => !p?.isLead);

  // If the event is all day, we always want the event to fall on the date
  // regardless of timezone. And timezone is a required field for creating a
  // project, so we can assume it will always be there.
  const start = allDay ? dayjs(startStr) : dayjs(startStr).tz(displayTz);
  const end = allDay ? dayjs(endStr) : dayjs(endStr).tz(displayTz);
  const projectDate = start ? dayjs(start).utc().format('dddd, MMMM D') : null;

  const whenType = allDay ? 'datespan' : 'timespan';
  const shortTz = useMemo(() => dayjs(start).format('z'), [start]);
  const sameDay = useMemo(() => {
    // If no end string, assume it's an event that falls on the same day.
    if (!endStr || endStr === '') return true;
    if (whenType === 'timespan') return start.isSame(end, 'day');
    // For all day events, end time is the next day but at 00:00:00, so subtract
    // 1 day to see if it's the same day.
    return end.subtract(1, 'day').isSame(start, 'day');
  }, [whenType]);

  const addressLines = useMemo(() => {
    const formattedAddress = location?.formattedAddress || '';

    if (!location?.data) return { addressLine1: formattedAddress, addressLine2: '' };

    try {
      const parsed = JSON.parse(location.data);
      const get = (type) =>
        parsed?.address_components.find(c => c.types.includes(type))?.short_name || '';

      const locality = get('locality');
      const region = get('administrative_area_level_1');
      const postal = get('postal_code');
      const street = get('route');
      const streetNumber = get('street_number');
      const neighborhood = get('neighborhood');

      // Parse from HTML if possible; otherwise build manually.
      // eslint-disable-next-line no-undef
      let addressLine1 = (new DOMParser())
        .parseFromString(parsed?.adr_address, 'text/html')
        .querySelector('.street-address')?.textContent;
      if (!addressLine1) {
        addressLine1 = [streetNumber, street].filter(Boolean).join(', ');
      }

      const localityLabel = neighborhood && formattedAddress.includes(neighborhood)
        ? neighborhood
        : locality;
      let addressLine2 = [localityLabel, region].filter(Boolean).join(', ');
      if (postal) {
        addressLine2 += ` ${postal}`;
      }

      return { addressLine1, addressLine2 };
    } catch (e) {
      return { addressLine1: formattedAddress, addressLine2: '' };
    }
  }, [location]);

  return (
    <div className="event-card info-card">
      {image && (
        <div className="info-card__image-wrapper">
          <img
            className="info-card__image"
            src={image}
            alt={projectName}
          />
        </div>
      )}

      <div className="info-card__content">
        <div className="info-card__row">
          <div className="info-card__title ap-text">
            {projectName}
          </div>
        </div>

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={DateTimeIcon}
              alt="date-time icon"
            />
          </div>
          <div>
            {projectDate ? (
              <EventDateTimeDisplay
                start={start}
                end={end}
                timeFormat={timeFormat}
                shortTz={shortTz}
                whenType={whenType}
                sameDay={sameDay}
              />
            ): '-'}
          </div>
        </div>

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={LocationIcon}
              alt="location icon"
            />
          </div>
          <div>
            {venue && <div>{venue}</div>}
            {addressLines.addressLine1 && (
              <div className="info-card__formatted-address">
                <div>
                  {addressLines.addressLine1}
                  {addressLines.addressLine2 ? ',' : ''}
                </div>
                {addressLines.addressLine2 && <div>{addressLines.addressLine2}</div>}
              </div>
            )}
            {!addressLines.addressLine1 && location.formattedAddress ? (
              <div className="info-card__formatted-address">
                {location.formattedAddress}
              </div>
            ) : null}
            {!venue && !addressLines.addressLine1 && !location.formattedAddress && '-'}
          </div>
        </div>

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={ProjectServiceTypeIcon}
              alt="service type icon"
            />
          </div>
          <div>{serviceType || '-'}</div>
        </div>

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={GuestCountIcon}
              alt="guest count icon"
            />
          </div>
          <div>{`${guestCount} Guest${guestCount === 1 ? '' : 's'}`}</div>
        </div>

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={ProjectLeadIcon}
              alt="lead icon"
            />
          </div>
          <div>{lead?.name || '-'}</div>
        </div>

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={LeadAssignmentIcon}
              alt="partners icon"
            />
          </div>
          <div>
            {otherPartners.length > 0
              ? otherPartners.map(p => p.name).join(', ')
              : '-'
            }
          </div>
        </div>
      </div>

      {projectId && (
        <a
          className="button"
          href={`/app/project/${projectId}/dashboard`}
        >
          View Project
        </a>
      )}
    </div>
  );
};

export default BookedProjectEventCard;
